import { Link } from 'react-router';
import { Check } from 'lucide-react';
import { BrandWordmark } from '../../../components/ui/brand-wordmark/index.js';
import { useQuotaQuery } from '../../../hooks/use-quota-query.js';

interface EditorHeaderProps {
  imageUrl: string | undefined;
  imageMeta: { width: number; height: number } | null;
  saved?: boolean;
  /**
   * Whether the session has a deliverable result the agent can receive.
   * True once at least one submit has produced a result on the server —
   * either accepted (iterating state) OR pending review (awaiting_acceptance,
   * which the server auto-snapshots on Finish). False before any submit.
   */
  canFinish?: boolean;
  /** True after the server's terminal 'finished' transition. Locks the button. */
  sessionFinished?: boolean;
  /** True while POST /finish is in flight. */
  isFinishing?: boolean;
  onFinish?: () => void;
}

/**
 * Editor top bar.
 *
 * Brand text + caret (matches the rest of the app's header), source-image
 * filename + format/dimensions crumb, and the user's current token balance
 * on the right. Deliberately information-dense at low contrast so it
 * yields screen real-estate to the canvas — no actions live here; Accept
 * and Revert are anchored in the bottom dock.
 */
export function EditorHeader({
  imageUrl,
  imageMeta,
  saved = false,
  canFinish = false,
  sessionFinished = false,
  isFinishing = false,
  onFinish,
}: EditorHeaderProps) {
  const { data } = useQuotaQuery();
  const tokens = data?.tokenBalance ?? null;
  const allocation = data?.monthlyAllocation ?? null;

  const filename = deriveFilename(imageUrl);
  const format = deriveFormat(imageUrl);
  const dims = imageMeta ? `${String(imageMeta.width)}×${String(imageMeta.height)}` : null;

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-5)',
        padding: '14px 24px',
        borderBottom: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-canvas)',
      }}
      data-testid="editor-header"
    >
      <Link
        to="/dashboard"
        className="inline-flex items-center whitespace-nowrap text-body outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400"
      >
        <BrandWordmark />
        <span aria-hidden="true" className="agb-logo-caret" />
      </Link>

      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '18px',
          backgroundColor: 'var(--color-border)',
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--font-mono)',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          minWidth: 0,
          flex: 1,
        }}
      >
        <span
          style={{
            color: 'var(--color-text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '32ch',
          }}
          title={imageUrl ?? undefined}
        >
          {filename}
        </span>
        {format && (
          <>
            <span style={{ color: 'var(--color-border-bright)' }}>·</span>
            <span style={{ color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
              {format}
            </span>
          </>
        )}
        {dims && (
          <>
            <span style={{ color: 'var(--color-border-bright)' }}>·</span>
            <span style={{ color: 'var(--color-text-secondary)' }}>{dims}</span>
          </>
        )}
        {saved && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: '10px',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
            }}
            data-testid="editor-saved-indicator"
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: '5px',
                height: '5px',
                backgroundColor: 'var(--color-accent-lime)',
                borderRadius: '50%',
              }}
            />
            delivered
          </span>
        )}
      </div>

      {tokens !== null && allocation !== null && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            whiteSpace: 'nowrap',
          }}
          aria-label={`${String(tokens)} of ${String(allocation)} tokens remaining`}
        >
          <b style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{tokens}</b>
          <span style={{ color: 'var(--color-text-muted)' }}> / {allocation} tokens</span>
        </div>
      )}

      {onFinish && (
        <FinishButton
          enabled={canFinish && !sessionFinished}
          finished={sessionFinished}
          loading={isFinishing}
          onClick={onFinish}
        />
      )}
    </header>
  );
}

interface FinishButtonProps {
  enabled: boolean;
  finished: boolean;
  loading: boolean;
  onClick: () => void;
}

function FinishButton({ enabled, finished, loading, onClick }: FinishButtonProps) {
  const disabled = !enabled || loading || finished;
  const label = finished ? 'Delivered' : loading ? 'Finishing…' : 'Finish';
  const title = finished
    ? 'Result delivered — you can close this tab.'
    : enabled
      ? 'Deliver the latest accepted result to the agent and end this session.'
      : 'Submit at least one mask first.';
  // Matches the dock's Generate button geometry (40px high, 6px corners,
  // 13px font, 120px min-width) so the two primary lime actions read as
  // a set across the editor.
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid="editor-finish-button"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        padding: '10px 16px',
        height: '40px',
        minWidth: '120px',
        borderRadius: '6px',
        fontFamily: 'var(--font-sans)',
        fontSize: '13px',
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        backgroundColor: finished
          ? 'rgba(221, 252, 116, 0.15)'
          : enabled
            ? 'var(--color-accent-lime)'
            : 'var(--color-border-bright)',
        color: finished
          ? 'var(--color-accent-lime)'
          : enabled
            ? '#0a0a0a'
            : 'var(--color-text-muted)',
        border: finished ? '1px solid rgba(221, 252, 116, 0.45)' : 0,
        transition: 'background-color 120ms',
      }}
    >
      <Check size={14} aria-hidden="true" />
      {label}
    </button>
  );
}

/**
 * Pull a human-friendly filename out of the source image URL. R2 keys for
 * editor sessions look like `mask-sessions/{sid}/source.png` and previously
 * generated images live at `generated/{uuid}/{slug}.jpeg` — strip query
 * strings (cache-busters) and return the last path segment.
 */
function deriveFilename(url: string | undefined): string {
  if (!url) return 'untitled';
  try {
    const parsed = new URL(url);
    const last = parsed.pathname.split('/').filter(Boolean).pop();
    return last ?? 'untitled';
  } catch {
    // Relative URL or malformed — fall back to the last path-ish segment.
    const stripped = url.split('?')[0] ?? url;
    const segments = stripped.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? 'untitled';
  }
}

function deriveFormat(url: string | undefined): string | null {
  if (!url) return null;
  const filename = deriveFilename(url);
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return null;
  const ext = filename.slice(dot + 1).toLowerCase();
  // Normalise common aliases so the header reads cleanly.
  if (ext === 'jpg') return 'jpeg';
  if (ext.length <= 4 && /^[a-z]+$/.test(ext)) return ext;
  return null;
}
